/**
 * Booking + pre-booking modals for a public stylist profile.
 *
 * `BookingModal` — pick a service → optional preferred date/time note →
 * optional notes → POST /bookings. Payment stays in Batch 4 (Razorpay
 * Route). Booking lands in the stylist's inbox as `pending`.
 *
 * `PrebookChatModal` — three free questions before booking is required.
 * After 3 user messages the composer locks and points at BookingModal.
 * If the user has an existing booking the chat unlocks fully.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, MessageSquare, Send, Lock, ArrowRight, CreditCard } from "lucide-react";
import { SERVICE_KIND_META } from "@/pages/stylists/StudioTabs";

// Lazy-load the Razorpay Checkout script once per session.
function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Razorpay script failed to load"));
    document.body.appendChild(s);
  });
}

export function BookingModal({ open, onClose, stylist, onBooked }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [preferredAt, setPreferredAt] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !stylist?.id) return;
    setLoading(true);
    api.get(`/stylists/${stylist.id}/services`)
      .then(({ data }) => {
        setServices(data.items || []);
        setSelected((data.items || [])[0] || null);
      })
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, [open, stylist?.id]);

  // Kick off Razorpay Checkout for the freshly-created booking. If the
  // stylist priced it as ₹0 (rare — e.g. a free intro consult) we skip
  // payment entirely and treat the booking as sent-for-review.
  const startPayment = async (booking) => {
    if (!booking?.price_inr || booking.price_inr <= 0) {
      toast.success("Request sent — stylist will confirm.");
      onBooked?.(booking);
      onClose?.();
      return;
    }
    try {
      const { data: cfg } = await api.get("/payments/razorpay/config");
      if (!cfg.ready) {
        toast.info("Payments aren't live yet — the stylist will share a link.");
        onBooked?.(booking);
        onClose?.();
        return;
      }
      const { data: order } = await api.post(`/bookings/${booking.id}/razorpay/order`);
      await loadRazorpayScript();
      const rzp = new window.Razorpay({
        key: cfg.key_id,
        amount: order.amount,
        currency: order.currency,
        name: "Stylebay",
        description: order.service_title || "Stylist consultation",
        order_id: order.order_id,
        handler: async (resp) => {
          try {
            const { data } = await api.post(`/bookings/${booking.id}/razorpay/verify`, resp);
            toast.success("Booking confirmed — payment received.");
            onBooked?.({ ...booking, ...data, payment_status: "paid" });
            onClose?.();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Payment verification failed.");
          }
        },
        modal: {
          ondismiss: () => {
            toast.info("Payment cancelled — booking held as unpaid.");
            onBooked?.(booking);
            onClose?.();
          },
        },
        prefill: { name: stylist?.display_name ? undefined : undefined },
        theme: { color: "#006666" },
      });
      rzp.open();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start payment.");
      // Booking is created but unpaid — surface it to the caller so they
      // can chase the stylist for a manual link if needed.
      onBooked?.(booking);
      onClose?.();
    }
  };

  const submit = async () => {
    if (!selected) { toast.error("Pick a service."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        stylist_id: stylist.id,
        service_id: selected.id,
        preferred_at: preferredAt || undefined,
        notes: notes || undefined,
      });
      setPreferredAt(""); setNotes("");
      // Immediately open the Razorpay Checkout so users don't fall out of
      // the flow while waiting for the stylist to reply.
      await startPayment(data.booking);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="booking-modal" className="max-w-md rounded-none">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Book {stylist?.display_name}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="animate-spin" /></div>
        ) : services.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {stylist?.display_name} hasn&apos;t published any consultation services yet.
          </p>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-2">Service</p>
              <div className="space-y-2" data-testid="booking-services">
                {services.map((s) => {
                  const M = SERVICE_KIND_META[s.kind] || {};
                  const Icon = M.icon;
                  const active = selected?.id === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      data-testid={`booking-service-${s.id}`}
                      onClick={() => setSelected(s)}
                      className={`w-full border p-3 flex items-center gap-3 text-left transition-colors ${
                        active ? "border-accent bg-accent/5" : "border-border hover:border-foreground"
                      }`}
                    >
                      {Icon && <Icon size={16} className="shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-sm">{s.title}</p>
                        <p className="text-xs text-muted-foreground">{s.duration_min} min</p>
                      </div>
                      <p className="text-sm font-serif">₹{s.price_inr.toLocaleString("en-IN")}</p>
                    </button>
                  );
                })}
              </div>
            </div>
            <Input
              data-testid="booking-preferred-at"
              placeholder="Preferred date & time (e.g., Sat 26 Jul, 4 PM)"
              value={preferredAt}
              onChange={(e) => setPreferredAt(e.target.value)}
              className="rounded-none h-11"
              maxLength={200}
            />
            <Textarea
              data-testid="booking-notes"
              placeholder="Anything the stylist should know? (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={800}
              className="rounded-none"
            />
            <p className="text-[11px] text-muted-foreground">
              You&apos;ll be redirected to secure Razorpay Checkout to confirm the booking.
              Payments in test mode use a demo card — no charge to your account.
            </p>
            <Button
              data-testid="booking-submit"
              onClick={submit}
              disabled={busy || !selected}
              className="w-full rounded-none h-11 uppercase tracking-[0.2em] text-[10px] gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <CreditCard size={14} />}
              Book &amp; pay {selected ? `₹${selected.price_inr.toLocaleString("en-IN")}` : ""}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function PrebookChatModal({ open, onClose, stylist, onBookNow }) {
  const [state, setState] = useState({ messages: [], questions_used: 0, questions_limit: 3, locked: false, unlocked_by_booking: false });
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    if (!open || !stylist?.id) return;
    setLoading(true);
    api.get(`/stylists/${stylist.id}/prebook`)
      .then(({ data }) => setState(data))
      .catch(() => setState({ messages: [], questions_used: 0, questions_limit: 3, locked: false, unlocked_by_booking: false }))
      .finally(() => setLoading(false));
  };
  useEffect(load, [open, stylist?.id]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      await api.post(`/stylists/${stylist.id}/prebook`, { message: msg });
      setInput("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "prebook_locked") {
        toast.info(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not send.");
      }
      load();
    } finally { setSending(false); }
  };

  const remaining = Math.max(0, (state.questions_limit || 3) - (state.questions_used || 0));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="prebook-modal" className="max-w-md rounded-none">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">Ask {stylist?.display_name}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          {state.unlocked_by_booking
            ? "Chat is unlocked — you have an active booking with this stylist."
            : `${remaining} of ${state.questions_limit} free questions remaining before booking.`}
        </p>
        <div className="mt-3 border border-border p-3 h-64 overflow-y-auto space-y-2 bg-muted/20" data-testid="prebook-messages">
          {loading ? (
            <Loader2 className="animate-spin mx-auto my-8" />
          ) : state.messages.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">Kick things off with a question — availability, budget, wedding date, etc.</p>
          ) : (
            state.messages.map((m) => (
              <div key={m.id || m.created_at} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-accent text-accent-foreground" : "bg-background border border-border"
                }`}>
                  {m.content}
                </div>
              </div>
            ))
          )}
        </div>
        {state.locked && !state.unlocked_by_booking ? (
          <div className="mt-3 border border-border p-4 text-center bg-muted/20">
            <Lock size={16} className="mx-auto text-muted-foreground" />
            <p className="text-sm mt-2">You&apos;ve used all {state.questions_limit} free questions.</p>
            <Button onClick={onBookNow} data-testid="prebook-book-now" className="mt-3 rounded-none h-10 uppercase tracking-[0.2em] text-[10px] gap-2">
              Book to keep chatting <ArrowRight size={12} />
            </Button>
          </div>
        ) : (
          <div className="mt-3 flex gap-2">
            <Input
              data-testid="prebook-input"
              placeholder="Type your question…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              maxLength={500}
              className="rounded-none h-10"
              disabled={sending}
            />
            <Button data-testid="prebook-send" onClick={send} disabled={!input.trim() || sending} className="rounded-none h-10 w-10 p-0">
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
